import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/useToast';
import { useGenerateStrategicReportMutation, StrategicReportResponse } from '@/lib/api-extensions/admin-queries';
import { TrendingUp, Calendar } from 'lucide-react';
import { subMonths } from 'date-fns';
import { useAtomValue } from 'jotai';
import { googleAdsAccountAtom } from '@/store';
import { cn } from '@/lib/utils';

interface StrategicReportGeneratorProps {
  className?: string;
  organizationId: number | null;
  businessId: number | null;
}

export function StrategicReportGenerator({ className, organizationId, businessId }: StrategicReportGeneratorProps) {
  const [response, setResponse] = useState<StrategicReportResponse | null>(null);
  const { toast } = useToast();
  
  // Get the currently selected Google Ads account
  const currentGoogleAdsAccount = useAtomValue(googleAdsAccountAtom);
  
  const generateStrategicReportMutation = useGenerateStrategicReportMutation();
  
  const handleGenerate = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Please select an organization',
        variant: 'destructive',
      });
      return;
    }

    if (!businessId) {
      toast({
        title: 'Error',
        description: 'Please select a business',
        variant: 'destructive',
      });
      return;
    }

    if (!currentGoogleAdsAccount) {
      toast({
        title: 'Error',
        description: 'No Google Ads account is currently selected. Please select a Google Ads account first.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await generateStrategicReportMutation.mutateAsync({
        organizationId: organizationId,
        googleadsaccountId: parseInt(currentGoogleAdsAccount.id, 10),
        businessId: businessId,
      });
      
      setResponse(result);
      
      toast({
        title: 'Success',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate strategic report',
        variant: 'destructive',
      });
    }
  };

  // Calculate date ranges for 6-month period
  const dateInfo = useMemo(() => {
    const endDate = new Date();
    const startDate = subMonths(endDate, 6);
    
    return {
      start: startDate,
      end: endDate,
      duration: 6, // months
    };
  }, []);

  const formatDateRange = (start: Date, end: Date) => {
    const startStr = start.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    const endStr = end.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    return `${startStr} - ${endStr}`;
  };
  
  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="px-6 py-6">
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Generate Strategic Report
        </CardTitle>
        <CardDescription className="mt-2">
          Generate a comprehensive strategic analysis report covering the last 6 months.
          This report includes long-term trend analysis, competitor insights, and strategic recommendations.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6 pt-6">
        
        {/* Report Info */}
        <div className="rounded-lg border bg-gray-50 p-4 space-y-3">
            <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
            <div className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span><strong>Analysis Period:</strong> {formatDateRange(dateInfo.start, dateInfo.end)} ({dateInfo.duration} months)</span>
            </div>
            </div>
            <div className="text-xs text-blue-600">
            Fixed 6-month lookback period for strategic analysis
            </div>
        </div>
        
        {/* Generate Button */}
        <Button 
          onClick={handleGenerate}
          disabled={generateStrategicReportMutation.isPending || !organizationId || !businessId || !currentGoogleAdsAccount}
          className="w-full"
        >
          {generateStrategicReportMutation.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Generating Strategic Report...
            </>
          ) : (
            <>
              <TrendingUp className="mr-2 h-4 w-4" />
              Generate Strategic Report
            </>
          )}
        </Button>
        
        {/* Estimated Time */}
        <div className="text-center">
          <p className="text-sm text-gray-700">Estimated processing time: ~15 minutes</p>
        </div>
        
        {/* Response Display */}
        {response && (
          <Alert className="border-green-200 bg-green-50">
            <div className="space-y-3">
              <AlertDescription className="text-sm">
                <strong>{response.message}</strong>
              </AlertDescription>
              
              <div className="text-sm text-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4" />
                  <span className="font-medium">Strategic report creation has been queued successfully.</span>
                </div>
                
                {/* Show generated date range */}
                {dateInfo && (
                  <div className="bg-green-100 rounded-md p-3 mb-3">
                    <div className="font-medium mb-1">Report Period:</div>
                    <div className="text-xs space-y-1">
                      <div>📊 Report: {formatDateRange(dateInfo.start, dateInfo.end)} ({dateInfo.duration} months)</div>
                      <div>🎯 Analysis: Strategic performance trends and executive insights</div>
                    </div>
                  </div>
                )}
                
                <div className="pl-6 space-y-1">
                  <p>📊 Strategic report will be generated in the background and may take up to 15 minutes to complete.</p>
                  <p>📧 The completed report will be delivered via notification once processing is finished.</p>
                  <p>🔄 This comprehensive analysis includes 6-month trend analysis, strategic recommendations, and optimization opportunities.</p>
                </div>
              </div>
            </div>
          </Alert>
        )}
        

      </CardContent>
    </Card>
  );
}